/* Klodshelten theme runtime.
   Everything is a custom element so that markup returned by the Section
   Rendering API upgrades itself - no re-binding needed after a cart update. */

(function () {
  'use strict';

  const KH = (window.KH = window.KH || {});

  KH.root = function () {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : root + '/';
  };

  KH.money = function (cents) {
    const format = KH.moneyFormat || '{{amount_with_comma_separator}} kr.';
    const value = (Number(cents) / 100).toFixed(2);
    const parts = value.split('.');
    const withComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1];
    const withDot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    const noDecimalComma = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const noDecimalDot = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimalDot)
      .replace(/\{\{\s*amount\s*\}\}/g, withDot);
  };

  /* snippets/image.liquid holds back the URLs of other sets' images on product
     pages, so Google's render doesn't see them as candidates for the result
     thumbnail. Bound to real input on purpose: a timer or an observer would
     also fire in Google's render. Cart swaps and fetched cards can bring in
     more held images afterwards, so the observer covers those. */
  if (document.body.classList.contains('template-product')) {
    const releaseImages = function (root) {
      root.querySelectorAll('img[data-kh-src]').forEach(function (img) {
        if (img.dataset.khSrcset) img.srcset = img.dataset.khSrcset;
        img.src = img.dataset.khSrc;
        img.removeAttribute('data-kh-srcset');
        img.removeAttribute('data-kh-src');
      });
    };
    /* Not `scroll`: a resize or a layout shift can fire it without any input. */
    const inputs = ['pointerdown', 'pointermove', 'touchstart', 'wheel', 'keydown'];
    const onFirstInput = function () {
      inputs.forEach(function (name) {
        window.removeEventListener(name, onFirstInput, true);
      });
      releaseImages(document);
      new MutationObserver(function (records) {
        if (records.some(function (record) { return record.addedNodes.length; })) {
          releaseImages(document);
        }
      }).observe(document.body, { childList: true, subtree: true });
    };
    inputs.forEach(function (name) {
      window.addEventListener(name, onFirstInput, { capture: true, passive: true });
    });
  }

  KH.t = function (key, replacements) {
    let string = (KH.strings && KH.strings[key]) || '';
    if (replacements) {
      Object.keys(replacements).forEach(function (token) {
        string = string.replace(new RegExp('\\{\\{\\s*' + token + '\\s*\\}\\}', 'g'), replacements[token]);
      });
    }
    return string;
  };

  /* ---------- Live region ---------- */

  let liveRegion = null;

  KH.announce = function (message) {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.getElementById('kh-live-region');
    }
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 60);
  };

  KH.toast = function (message, variant) {
    const host = document.getElementById('kh-toasts');
    KH.announce(message);
    if (!host) return;
    const node = document.createElement('div');
    node.className = 'kh-toast' + (variant ? ' kh-toast--' + variant : '');
    node.textContent = message;
    host.appendChild(node);
    window.setTimeout(function () {
      node.classList.add('is-leaving');
      window.setTimeout(function () {
        node.remove();
      }, 300);
    }, 3600);
  };

  /* ---------- Section rendering helpers ---------- */

  /* The cart drawer is itself a section root, so a naive replaceWith() swapped
     the live <dialog> for a freshly rendered, closed one: changing a quantity
     closed the drawer, threw focus to <body>, never fired the `close` event
     and therefore left the scroll lock on the page. An open dialog keeps its
     element and only has its contents swapped, so it stays in the top layer.

     Whatever had focus inside the replaced markup is gone afterwards, so the
     control is identified by data-focus-key and refocused on the new node.
     Line keys are positional, so after removing line 2 focus lands on the item
     that moved up into its place - the same thing that happens visually. */

  function focusKeyOf(node) {
    if (!node || !node.getAttribute) return null;
    return node.getAttribute('data-focus-key');
  }

  function restoreFocusKey(key) {
    if (!key) return;
    const exact = document.querySelector('[data-focus-key="' + key + '"]');
    if (exact) {
      exact.focus();
      return;
    }
    /* The row it belonged to is gone - the last remaining row is the nearest
       thing to where the shopper was. */
    const scope = document.querySelector('dialog[open]') || document;
    const rows = scope.querySelectorAll('[data-cart-remove]');
    if (rows.length) {
      rows[rows.length - 1].focus();
      return;
    }
    if (scope === document) return;
    /* Direct, not focusFirst(): that waits for an animation frame, which a
       backgrounded tab never delivers. The node is already in the document. */
    const fallback = scope.querySelector(FOCUSABLE);
    if (fallback) fallback.focus();
  }

  KH.replaceSections = function (sections) {
    if (!sections) return;
    const activeKey = focusKeyOf(document.activeElement);
    Object.keys(sections).forEach(function (id) {
      const html = sections[id];
      if (typeof html !== 'string') return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('[data-section-id="' + id + '"]');
      if (!source) return;
      document.querySelectorAll('[data-section-id="' + id + '"]').forEach(function (target) {
        const live = target.matches('dialog[open]') ? target : target.querySelector('dialog[open]');
        const incoming = live ? source.querySelector('dialog') : null;
        if (live && incoming) {
          /* The drawer stays open while its contents are rebuilt from scratch,
             so the body's scroll position has to be carried across by hand -
             changing the quantity on the eighth line used to throw the list
             back to the top, with the line you were working on off screen.
             `data-swapped` lets the new contents fade in over the frame the
             swap lands in rather than appearing in one step. */
          const previous = live.querySelector('[data-drawer-scroll]');
          const offset = previous ? previous.scrollTop : 0;
          live.replaceChildren(...document.importNode(incoming, true).childNodes);
          const current = live.querySelector('[data-drawer-scroll]');
          if (current && offset) current.scrollTop = offset;
          live.setAttribute('data-swapped', '');
          window.clearTimeout(live.khSwapTimer);
          live.khSwapTimer = window.setTimeout(function () {
            live.removeAttribute('data-swapped');
          }, 500);
        } else {
          target.replaceWith(source.cloneNode(true));
        }
      });
    });
    /* A rebuilt cart comes back with the terms box unticked and the express
       buttons locked; put the shopper's choice back. */
    if (KH.applyTerms) KH.applyTerms();
    const lost = !document.activeElement || document.activeElement === document.body;
    if (activeKey && lost) restoreFocusKey(activeKey);
  };

  KH.cartSections = function () {
    const ids = [];
    document.querySelectorAll('[data-cart-section]').forEach(function (node) {
      const id = node.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  };

  KH.refreshCart = function (state) {
    document.dispatchEvent(new CustomEvent('kh:cart:updated', { detail: state || {} }));
  };

  KH.cartRequest = function (url, body) {
    const sections = KH.cartSections();
    if (sections.length) body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
    return fetch(KH.root() + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          const error = new Error(data.description || data.message || KH.t('cart_error'));
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  };

  /* ---------- Environment metrics ---------- */

  function setScrollbarWidth() {
    const width = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', width + 'px');
  }

  function setStickyOffset() {
    const header = document.querySelector('[data-sticky-header]');
    const height = header ? header.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--sticky-offset', Math.round(height) + 'px');
  }

  function setViewportUnit() {
    document.documentElement.style.setProperty('--vh', window.innerHeight * 0.01 + 'px');
  }

  /* A sticky column taller than the window keeps its bottom out of reach until
     the page ends, so `[data-sticky-fit]` only sticks while it fits - measured
     against the header's full height, since the header comes back the moment
     the visitor scrolls up. */
  const stickyFitObserved = new WeakSet();
  let stickyFitObserver = null;

  function updateStickyFit(element) {
    const header = document.querySelector('[data-sticky-header]');
    const offset = header ? header.getBoundingClientRect().height : 0;
    element.toggleAttribute('data-sticky-fits', element.offsetHeight + offset + 32 <= window.innerHeight);
  }

  function initStickyFit(root) {
    root.querySelectorAll('[data-sticky-fit]').forEach(function (element) {
      updateStickyFit(element);
      if (stickyFitObserved.has(element) || !('ResizeObserver' in window)) return;
      if (!stickyFitObserver) {
        stickyFitObserver = new ResizeObserver(function (entries) {
          entries.forEach(function (entry) {
            updateStickyFit(entry.target);
          });
        });
      }
      stickyFitObserver.observe(element);
      stickyFitObserved.add(element);
    });
  }

  function measure() {
    setScrollbarWidth();
    setStickyOffset();
    setViewportUnit();
    initStickyFit(document);
  }

  /* ---------- Reveal on scroll ---------- */

  let revealObserver = null;
  let revealQueue = [];
  let revealTimer = null;

  /* Rows, not elements.

     An IntersectionObserver reports every card in a grid row in the same
     delivery, but each one was then revealed on a timer of its own, staggered
     by its index in the list. A row of four therefore built itself left to
     right, and because the stagger counted items rather than columns the
     spacing changed with the breakpoint - four across on a desktop meant the
     second row started before the first had finished.

     The batch is grouped by the top edge the elements sit on, everything in a
     row is revealed in the same frame, and the stagger is handed to CSS as a
     delay so the browser schedules the cascade instead of a pile of drifting
     setTimeouts. */
  function flushReveals() {
    revealTimer = null;
    const batch = revealQueue;
    revealQueue = [];
    if (!batch.length) return;

    const rows = [];
    batch
      .map(function (node) {
        return { node: node, top: node.getBoundingClientRect().top };
      })
      .sort(function (a, b) {
        return a.top - b.top;
      })
      .forEach(function (item) {
        const row = rows[rows.length - 1];
        /* 24px of slack: cards in one row can differ by a pixel or two when a
           neighbour has a taller badge, while a genuinely separate row is
           always further away than that. */
        if (row && Math.abs(item.top - row.top) < 24) {
          row.nodes.push(item.node);
        } else {
          rows.push({ top: item.top, nodes: [item.node] });
        }
      });

    rows.forEach(function (row, index) {
      /* Capped, so a long batch does not leave its last row waiting a second
         for a turn that the eye stopped expecting. */
      const delay = Math.min(index, 5) * 90;
      row.nodes.forEach(function (node) {
        const explicit = node.getAttribute('data-reveal-delay');
        const ms = explicit === null ? delay : Number(explicit);
        node.style.setProperty('--reveal-delay', ms + 'ms');
        node.classList.add('is-revealed');
      });
    });
  }

  function queueReveal(node) {
    revealQueue.push(node);
    if (revealTimer !== null) return;
    /* A timer rather than an animation frame: a backgrounded tab is never
       handed a frame, and the page would sit there invisible in one. */
    revealTimer = window.setTimeout(flushReveals, 0);
  }

  function observeReveals(scope) {
    const nodes = (scope || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (node) {
        node.classList.add('is-revealed');
      });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            queueReveal(entry.target);
            revealObserver.unobserve(entry.target);
          });
        },
        /* Starting a little before the element enters the viewport means the
           animation is already under way by the time it is looked at, which
           is what separates a page that settles from one that snaps. */
        { rootMargin: '140px 0px -6% 0px', threshold: 0.04 }
      );
    }

    /* Nothing special is needed for the first screen any more: everything on
       it intersects at once, arrives as a single batch, and is cascaded by row
       like every batch after it. */
    nodes.forEach(function (node) {
      revealObserver.observe(node);
    });
  }

  KH.observeReveals = observeReveals;

  /* ---------- Hover prefetch ---------- */

  /* Chromium reads the speculation rules in the layout and needs nothing from
     here. Firefox ignores that tag, so this does the same job the only way it
     can: on hover or first touch, drop a <link rel="prefetch"> for the
     document. It is deliberately modest - one link per href, capped, cancelled
     if the pointer leaves before the dwell time, and skipped entirely when the
     browser reports a slow connection or the visitor has asked to save data. */
  function initHoverPrefetch() {
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) return;
    const link = document.createElement('link');
    if (!link.relList || !link.relList.supports || !link.relList.supports('prefetch')) return;

    const connection = navigator.connection;
    if (connection) {
      if (connection.saveData) return;
      if (/2g/.test(connection.effectiveType || '')) return;
    }

    const seen = new Set();
    const LIMIT = 12;
    let timer = null;

    function prefetch(href) {
      if (seen.size >= LIMIT || seen.has(href)) return;
      seen.add(href);
      const tag = document.createElement('link');
      tag.rel = 'prefetch';
      tag.as = 'document';
      tag.href = href;
      document.head.appendChild(tag);
    }

    function candidate(target) {
      const anchor = target.closest ? target.closest('a[href]') : null;
      if (!anchor) return null;
      if (anchor.hasAttribute('data-no-prefetch')) return null;
      if (anchor.target && anchor.target !== '_self') return null;
      if (anchor.hasAttribute('download')) return null;
      if ((anchor.getAttribute('rel') || '').indexOf('nofollow') !== -1) return null;

      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch (error) {
        return null;
      }
      if (url.origin !== window.location.origin) return null;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
      /* Same exclusions as the speculation rules: anything that carries or
         changes state is left to load when it is genuinely asked for. */
      if (/^\/(cart|checkouts|account|tools)(\/|$)/.test(url.pathname)) return null;
      return url.href;
    }

    document.addEventListener(
      'pointerenter',
      function (event) {
        if (event.pointerType === 'touch') return;
        const href = candidate(event.target);
        if (!href) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          prefetch(href);
        }, 65);
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'pointerleave',
      function () {
        window.clearTimeout(timer);
      },
      { capture: true, passive: true }
    );

    /* On touch there is no hover, and a quick mouse click can beat the dwell
       timer, so the press itself is the signal - it still buys the time
       between button/finger-down and the click. */
    document.addEventListener(
      'mousedown',
      function (event) {
        if (event.button !== 0) return;
        const href = candidate(event.target);
        if (href) prefetch(href);
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'touchstart',
      function (event) {
        const href = candidate(event.target);
        if (href) prefetch(href);
      },
      { capture: true, passive: true }
    );
  }

  /* ---------- Focus management ---------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function focusFirst(container) {
    const target = container.querySelector('[data-autofocus]') || container.querySelector(FOCUSABLE);
    if (target) {
      window.requestAnimationFrame(function () {
        target.focus();
      });
    }
  }

  function lockScroll(locked) {
    const open = document.querySelectorAll('dialog[open][data-lock-scroll]').length;
    document.documentElement.classList.toggle('kh-scroll-locked', locked || open > 0);
  }

  /* ---------- <kh-dialog> ---------- */

  class KhDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.opener = null;
      this.dialog.setAttribute('data-lock-scroll', '');

      this.onCancel = this.onCancel.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onBackdrop = this.onBackdrop.bind(this);

      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('mousedown', this.onBackdrop);

      /* Delegated, because the drawer's contents are re-rendered on every cart
         change - a listener bound to the close button itself would be thrown
         away with it the first time a quantity changed. */
      this.dialog.addEventListener('click', (event) => {
        /* A dialog can sit inside another one (the terms popup lives in the
           cart drawer), and its clicks bubble up through the outer dialog;
           only a close button of this dialog's own closes it. */
        const closer = event.target.closest('[data-dialog-close]');
        if (closer && closer.closest('dialog') === this.dialog) this.close();
      });
    }

    open(opener) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.openerKey = focusKeyOf(this.opener);
      this.dialog.showModal();
      lockScroll(true);
      this.setAttribute('open', '');
      focusFirst(this.dialog);
      this.dispatchEvent(new CustomEvent('kh:dialog:open', { bubbles: true }));
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;
      this.dialog.close();
      /* The browser delivers `close` as a queued task, and a backgrounded tab
         can sit on it. Everything that unwinds the open state - the scroll
         lock above all - runs here as well, so the page can never be left
         unscrollable behind a drawer that is already gone. onClose() is
         idempotent, so the event firing afterwards changes nothing. */
      this.onClose();
    }

    onCancel(event) {
      event.preventDefault();
      this.close();
    }

    onClose() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      lockScroll(false);
      /* The opener is often the header cart button, which is re-rendered on
         every cart change - the node we were handed no longer exists. Its key
         still points at the button now standing in the same place, so closing
         the drawer returns to the control it was opened from rather than
         dropping the keyboard user at the top of the page. */
      let target = this.opener && document.contains(this.opener) ? this.opener : null;
      if (!target && this.openerKey) {
        target = document.querySelector('[data-focus-key="' + this.openerKey + '"]');
      }
      if (target) target.focus();
      this.dispatchEvent(new CustomEvent('kh:dialog:close', { bubbles: true }));
    }

    onBackdrop(event) {
      if (event.target === this.dialog) this.close();
    }
  }

  customElements.define('kh-dialog', KhDialog);

  KH.openDialog = function (id, opener) {
    const node = document.getElementById(id);
    if (node && typeof node.open === 'function') {
      node.open(opener);
      return true;
    }
    return false;
  };

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-dialog-open]');
    if (!trigger) return;
    const id = trigger.getAttribute('data-dialog-open');
    const node = document.getElementById(id);
    if (!node || typeof node.open !== 'function') return;
    event.preventDefault();
    /* A trigger that sits inside a gallery hands over which photo is on
       screen. Without it, clicking the fourth photo to see it larger opened
       the lightbox on the first one. Plain dialogs ignore the second
       argument. */
    const gallery = trigger.closest('kh-gallery');
    /* A trigger outside the gallery (the sticky bar's photo) names its photo
       instead. */
    let index = gallery ? gallery.index : undefined;
    if (index === undefined && trigger.hasAttribute('data-dialog-index')) {
      index = Number(trigger.getAttribute('data-dialog-index'));
    }
    node.open(trigger, index);
  });

  /* ---------- Trusted external links ----------
     Shopify's storefront injects an "Are you sure you want to leave?" page
     (shopify.exit_interstitial) on every link to a host outside its own
     allowlist, and merchants cannot add to that list. It steps aside when the
     page has already cancelled the click, so links to our review profile and
     e-mærket certificate open the new tab themselves. Modified and middle
     clicks are left alone. */
  const TRUSTED_HOSTS = /(^|\.)(trustpilot\.com|emaerket\.dk)$/i;

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href][target="_blank"]');
    if (!link || !TRUSTED_HOSTS.test(link.hostname)) return;
    event.preventDefault();
    window.open(link.href, '_blank', 'noopener');
  });

  /* ---------- Search unfold ----------
     Opened from the header's search pill, the search panel grows out of the
     pill instead of popping up in the middle of the screen: it starts clipped
     to the pill's own shape and position, then unfolds to full size while the
     suggestions fade in underneath. Opened any other way (the phone's search
     icon, a keyboard shortcut), the panel keeps its usual pop. */

  document.addEventListener('kh:dialog:open', function (event) {
    const host = event.target;
    if (host.id !== 'kh-search-dialog') return;
    const pill = host.opener && host.opener.closest && host.opener.closest('.kh-header__searchpill');
    const panel = host.dialog;
    if (!pill || !panel || typeof panel.animate !== 'function') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* The pop animation transforms the panel, which would skew the measuring
       below; it stays off until the dialog closes. */
    panel.style.animation = 'none';
    const to = panel.getBoundingClientRect();
    const from = pill.getBoundingClientRect();
    const form = panel.querySelector('.kh-psearch__form');
    if (!to.width || !from.width) return;

    /* Lines the panel's search row up with the pill, then clips away
       everything outside the pill's outline. */
    const formHeight = form ? form.getBoundingClientRect().height : from.height;
    const clipTop = Math.max(0, (formHeight - from.height) / 2);
    const dy = from.top - to.top - clipTop;
    const left = Math.max(0, from.left - to.left);
    const right = Math.max(0, to.right - from.right);
    const bottom = Math.max(0, to.height - clipTop - from.height);
    const radius = getComputedStyle(panel).borderTopLeftRadius;
    const ease = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

    panel.animate(
      [
        {
          transform: 'translateY(' + dy + 'px)',
          clipPath: 'inset(' + clipTop + 'px ' + right + 'px ' + bottom + 'px ' + left + 'px round ' + from.height / 2 + 'px)',
        },
        { transform: 'none', clipPath: 'inset(0 0 0 0 round ' + radius + ')' },
      ],
      { duration: 460, easing: ease }
    );

    const body = panel.querySelector('.kh-psearch__body');
    if (body) {
      body.animate(
        [
          { opacity: 0, transform: 'translateY(-8px)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 320, delay: 180, easing: ease, fill: 'backwards' }
      );
    }
  });

  document.addEventListener('kh:dialog:close', function (event) {
    if (event.target.id === 'kh-search-dialog' && event.target.dialog) {
      event.target.dialog.style.animation = '';
    }
  });

  /* ---------- <kh-rail> ---------- */

  class KhRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-rail]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.rail) return;

      this.update = this.update.bind(this);
      this.rail.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);

      if (this.prev) this.prev.addEventListener('click', () => this.scrollByPage(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollByPage(1));

      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
    }

    step() {
      const item = this.rail.firstElementChild;
      if (!item) return 240;
      const styles = window.getComputedStyle(this.rail);
      const gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
      return item.getBoundingClientRect().width + gap;
    }

    scrollByPage(direction) {
      const step = this.step();
      const perPage = Math.max(1, Math.floor(this.rail.clientWidth / step));
      this.rail.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
    }

    update() {
      if (!this.rail) return;
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const overflowing = max > 4;
      this.toggleAttribute('data-overflowing', overflowing);
      if (this.prev) this.prev.hidden = !overflowing || this.rail.scrollLeft <= 4;
      if (this.next) this.next.hidden = !overflowing || this.rail.scrollLeft >= max - 4;
    }
  }

  customElements.define('kh-rail', KhRail);

  /* ---------- <kh-quantity> ---------- */

  class KhQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.steps = [...this.querySelectorAll('[data-quantity-step]')];
      this.steps.forEach((button) => {
        button.addEventListener('click', () => {
          const next = this.clamp(Number(this.input.value || 0) + Number(button.getAttribute('data-quantity-step')));
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      /* A button that silently refuses to do anything reads as a broken
         button. At either end of the range it is marked disabled instead, so
         the limit is visible before it is hit. */
      this.input.addEventListener('input', () => this.syncSteps());
      this.syncSteps();
    }

    bounds() {
      return {
        min: Number(this.input.min || 0),
        max: this.input.max ? Number(this.input.max) : Infinity,
      };
    }

    clamp(value) {
      const { min, max } = this.bounds();
      return Math.min(max, Math.max(min, value));
    }

    syncSteps() {
      const { min, max } = this.bounds();
      const value = Number(this.input.value || 0);
      this.steps.forEach((button) => {
        const step = Number(button.getAttribute('data-quantity-step'));
        button.disabled = step > 0 ? value >= max : value <= min;
      });
    }
  }

  customElements.define('kh-quantity', KhQuantity);

  /* ---------- <kh-add-to-cart> ---------- */

  class KhAddToCart extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.classList.toggle('is-busy', busy);
      this.button.disabled = busy;
      this.button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    onSubmit(event) {
      if (this.hasAttribute('data-no-ajax')) return;
      event.preventDefault();
      if (this.busy) return;
      this.busy = true;
      this.setBusy(true);

      const formData = new FormData(this.form);
      const body = {
        items: [
          {
            id: Number(formData.get('id')),
            quantity: Number(formData.get('quantity') || 1)
          }
        ]
      };

      const properties = {};
      formData.forEach(function (value, key) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && String(value).trim() !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) body.items[0].properties = properties;

      const selling_plan = formData.get('selling_plan');
      if (selling_plan) body.items[0].selling_plan = Number(selling_plan);

      KH.cartRequest('cart/add.js', body)
        .then((data) => {
          KH.replaceSections(data.sections);
          KH.refreshCart(data);
          if (KH.cartType === 'drawer' && !this.hasAttribute('data-no-drawer')) {
            KH.openDialog('kh-cart-drawer', this.button);
          }
          KH.toast(KH.t('cart_added'), 'success');
        })
        .catch((error) => {
          KH.toast(error.message || KH.t('cart_error'), 'error');
        })
        .finally(() => {
          this.busy = false;
          this.setBusy(false);
        });
    }
  }

  customElements.define('kh-add-to-cart', KhAddToCart);

  /* ---------- <kh-sticky-atc> ---------- */

  class KhStickyAtc extends HTMLElement {
    connectedCallback() {
      const scope = this.closest('[data-variant-cta]') || this.parentElement;
      this.target = scope && scope.querySelector('kh-add-to-cart [type="submit"]');
      if (!this.target) return;
      /* By class: the photo in front of it is a button too (it opens the
         lightbox). */
      this.querySelector('.kh-sticky-atc__button').addEventListener('click', () => this.target.click());
      this.observer = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', passed);
        this.inert = !passed;
      });
      this.observer.observe(this.target);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('kh-sticky-atc', KhStickyAtc);

  /* ---------- <kh-cart-items> ---------- */

  class KhCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    change(line, quantity) {
      this.setBusy(true);
      const removing = Number(quantity) === 0;
      KH.cartRequest('cart/change.js', { line: Number(line), quantity: Number(quantity) })
        .then(function (data) {
          KH.replaceSections(data.sections);
          KH.refreshCart(data);
          /* Nothing about a re-rendered cart reaches a screen reader on its
             own: the row simply stops existing. */
          KH.announce(removing ? KH.t('cart_removed') : KH.t('cart_updated'));
        })
        .catch(function (error) {
          KH.toast(error.message || KH.t('cart_error'), 'error');
        })
        .finally(() => {
          this.setBusy(false);
        });
    }

    onChange(event) {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      this.change(input.getAttribute('data-line'), input.value);
    }

    onClick(event) {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      this.change(remove.getAttribute('data-line'), 0);
    }
  }

  customElements.define('kh-cart-items', KhCartItems);

  /* ---------- <kh-cart-note> ---------- */

  class KhCartNote extends HTMLElement {
    connectedCallback() {
      this.field = this.querySelector('textarea');
      if (!this.field) return;
      let timer = null;
      this.field.addEventListener('input', () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          fetch(KH.root() + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: this.field.value })
          });
        }, 600);
      });
    }
  }

  customElements.define('kh-cart-note', KhCartNote);

  /* ---------- <kh-predictive-search> ---------- */

  class KhRotator extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      if (this.items.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var interval = parseInt(this.getAttribute('data-interval'), 10);
      if (!interval || interval < 800) interval = 3400;
      this.index = 0;
      this.timer = window.setInterval(this.next.bind(this), interval);
      this.addEventListener('kh:pause', this.stop.bind(this));
      document.addEventListener('visibilitychange', this.onVisibility.bind(this));
    }

    disconnectedCallback() {
      this.stop();
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    onVisibility() {
      if (document.hidden) this.stop();
    }

    next() {
      this.items[this.index].hidden = true;
      this.index = (this.index + 1) % this.items.length;
      var item = this.items[this.index];
      item.hidden = false;
    }
  }

  customElements.define('kh-rotator', KhRotator);

  class KhPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.idle = this.querySelector('[data-predictive-idle]');
      this.sectionId = this.getAttribute('data-section-id') || 'predictive-search';
      if (!this.input || !this.results) return;

      this.cache = new Map();
      this.controller = null;
      this.timer = null;

      this.input.addEventListener('input', this.onInput.bind(this));
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));

      this.reset = this.querySelector('[data-search-reset]');
      if (this.reset) {
        this.reset.addEventListener('click', () => {
          this.input.value = '';
          this.input.focus();
          this.clear();
        });
      }
      /* Nothing typed, nothing to clear: the button would otherwise sit in an
         empty field looking like a control that does nothing. */
      this.syncReset();
    }

    syncReset() {
      if (!this.reset) return;
      this.reset.hidden = this.input.value.trim() === '';
    }

    clear() {
      this.results.innerHTML = '';
      this.setExpanded(false);
      if (this.idle) this.idle.hidden = false;
      if (this.status) this.status.textContent = '';
      this.input.removeAttribute('aria-activedescendant');
      this.syncReset();
    }

    setExpanded(expanded) {
      this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.results.toggleAttribute('hidden', !expanded);
    }

    onInput() {
      const term = this.input.value.trim();
      window.clearTimeout(this.timer);
      this.syncReset();
      if (term.length < 2) {
        this.clear();
        return;
      }
      this.timer = window.setTimeout(() => this.search(term), 220);
    }

    search(term) {
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      if (this.status) this.status.textContent = KH.t('search_loading');
      this.classList.add('is-loading');

      const params = new URLSearchParams({
        q: term,
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku',
        section_id: this.sectionId
      });
      const types = this.getAttribute('data-resource-types');
      if (types) params.set('resources[type]', types);

      fetch(KH.root() + 'search/suggest?' + params.toString(), { signal: this.controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then((html) => {
          this.cache.set(term, html);
          this.render(html);
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') console.error(error);
        })
        .finally(() => {
          this.classList.remove('is-loading');
        });
    }

    render(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector('[data-predictive-content]');
      this.results.innerHTML = source ? source.innerHTML : '';
      /* The previous list is gone, so the option it pointed at is gone too. */
      this.input.removeAttribute('aria-activedescendant');
      if (this.idle) this.idle.hidden = true;
      this.setExpanded(true);
      if (this.status) {
        const count = this.results.querySelectorAll('[role="option"]').length;
        this.status.textContent = KH.t('search_results_count', { count: count });
      }
    }

    options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(event) {
      const options = this.options();
      if (event.key === 'Escape') {
        /* One Escape used to do two things at once: it put the suggestion
           list away and, because the list lives in a <dialog>, closed the
           whole search panel with it - so a shopper correcting a typo lost
           the search entirely. With suggestions open the first press only
           closes them and keeps what was typed; a second press closes the
           dialog, the way Escape does everywhere else in the theme. */
        if (this.input.getAttribute('aria-expanded') === 'true') {
          event.preventDefault();
          this.setExpanded(false);
          this.input.removeAttribute('aria-activedescendant');
          if (this.idle) this.idle.hidden = false;
        }
        return;
      }
      if (!options.length) return;
      const activeId = this.input.getAttribute('aria-activedescendant');
      let index = options.findIndex(function (option) {
        return option.id === activeId;
      });

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        index = (index + 1) % options.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        index = index <= 0 ? options.length - 1 : index - 1;
      } else if (event.key === 'Enter' && index >= 0) {
        const link = options[index].querySelector('a') || options[index].closest('a');
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
        return;
      } else {
        return;
      }

      options.forEach(function (option, i) {
        option.setAttribute('aria-selected', i === index ? 'true' : 'false');
        option.classList.toggle('is-active', i === index);
      });
      this.input.setAttribute('aria-activedescendant', options[index].id);
      options[index].scrollIntoView({ block: 'nearest' });
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      /* Collapsing a search that still has a term in it left the panel showing
         nothing at all - the results hidden and the popular terms hidden
         behind them. There is nothing underneath a dialog to uncover, so the
         list only closes once the field is empty. */
      window.setTimeout(() => {
        if (this.input.value.trim() !== '') return;
        if (!this.contains(document.activeElement)) this.setExpanded(false);
      }, 120);
    }
  }

  customElements.define('kh-predictive-search', KhPredictiveSearch);

  /* ---------- <kh-facets> ---------- */

  class KhFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      if (!this.querySelector('form')) return;

      this.debounce = null;

      // Listeners live on the element, not on the form: applying a filter
      // replaces the panel's markup, so anything bound to the form itself
      // would be listening to a detached node from the second click on.
      this.addEventListener('change', this.onChange.bind(this));
      // The grid holds forms too - every quick-add "Læg i kurv" card is one.
      // Their submits bubble up here, and reading them as filters re-rendered
      // the grid on every add to cart and piled each card's fields into the URL.
      this.addEventListener('submit', (event) => {
        if (!this.isFacetForm(event.target)) return;
        event.preventDefault();
        this.apply();
      });
      this.addEventListener('click', this.onClick.bind(this));
      window.addEventListener('popstate', () => {
        if (this.virtual) {
          this.renderVirtual(this.currentPage(), false);
          return;
        }
        this.render(window.location.search, false, false);
      });

      this.keepOpenOnDesktop();
      this.initVirtualOrder();
    }

    // Sold-out sets last, across the whole collection rather than inside each
    // page. Liquid can only order the page it was handed, so the page the
    // server rendered is rebuilt here from two native, availability-filtered
    // runs: the in-stock products first, the sold-out ones after. Everything
    // below is a no-op without the data attributes, and the server-rendered
    // page stays on screen if a request fails, so the ordering degrades to
    // per-page rather than to an empty grid.
    initVirtualOrder() {
      this.virtual = null;
      if (!this.hasAttribute('data-virtual-order')) return;

      const inStock = Number(this.getAttribute('data-instock'));
      const total = Number(this.getAttribute('data-total'));
      const per = Number(this.getAttribute('data-per-page'));
      if (!(inStock > 0) || !(total > inStock) || !(per > 0)) return;

      this.virtual = { inStock: inStock, total: total, per: per, cache: new Map(), token: 0 };
      this.renderVirtual(this.currentPage(), false);
    }

    currentPage() {
      const page = Number(new URLSearchParams(window.location.search).get('page'));
      return page > 0 ? page : 1;
    }

    // One native page of a single availability run, as an array of card markup.
    fetchRun(available, page) {
      const key = available + ':' + page;
      const cached = this.virtual.cache.get(key);
      if (cached) return cached;

      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      params.delete('filter.v.availability');
      params.append('filter.v.availability', available);
      params.set('page', String(page));
      params.set('section_id', this.sectionId);

      const request = fetch(window.location.pathname + '?' + params.toString())
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          return Array.from(parsed.querySelectorAll('[data-facets-results] .kh-grid > li')).map(function (item) {
            return item.outerHTML;
          });
        });

      this.virtual.cache.set(key, request);
      return request;
    }

    // `length` cards from one availability run, starting at `start`. A slice
    // never spans more than two native pages, so this costs at most two
    // requests per run and none at all once a page has been visited.
    async sliceRun(available, start, length) {
      const per = this.virtual.per;
      const cards = [];
      let cursor = start;

      while (cursor < start + length) {
        const page = Math.floor(cursor / per) + 1;
        const items = await this.fetchRun(available, page);
        if (!items.length) break;
        const offset = cursor - (page - 1) * per;
        const take = Math.min(per - offset, start + length - cursor, items.length - offset);
        if (take <= 0) break;
        cards.push.apply(cards, items.slice(offset, offset + take));
        cursor += take;
      }

      return cards;
    }

    async renderVirtual(page, push) {
      if (!this.virtual) return;
      const grid = this.querySelector('[data-facets-results] .kh-grid');
      if (!grid) return;

      const state = this.virtual;
      const token = ++state.token;
      const start = (page - 1) * state.per;
      const end = Math.min(start + state.per, state.total);
      if (start >= end) return;

      const liveStart = Math.min(start, state.inStock);
      const liveEnd = Math.min(end, state.inStock);
      const goneStart = Math.max(start - state.inStock, 0);
      const goneEnd = Math.max(end - state.inStock, 0);

      this.classList.add('is-loading');
      try {
        const cards = [];
        if (liveEnd > liveStart) {
          cards.push.apply(cards, await this.sliceRun('1', liveStart, liveEnd - liveStart));
        }
        if (goneEnd > goneStart) {
          cards.push.apply(cards, await this.sliceRun('0', goneStart, goneEnd - goneStart));
        }
        // A newer request has started, or nothing came back: keep what the
        // server rendered rather than replacing it with a gap.
        if (token !== state.token || !cards.length) return;
        grid.innerHTML = cards.join('');
        this.markPage(page);
        if (push) {
          const params = new URLSearchParams(window.location.search);
          if (page > 1) {
            params.set('page', String(page));
          } else {
            params.delete('page');
          }
          const query = params.toString();
          window.history.pushState({ page: page }, '', window.location.pathname + (query ? '?' + query : ''));
        }
        observeReveals(document);
      } catch (error) {
        console.error(error);
      } finally {
        if (token === state.token) this.classList.remove('is-loading');
      }
    }

    // The page count is the same either way, so the server's pager is reused;
    // only which button reads as current has to follow the virtual page.
    markPage(page) {
      this.querySelectorAll('.kh-pager__page').forEach((item) => {
        const label = (item.textContent || '').trim();
        const isCurrent = Number(label) === page;
        if (isCurrent && item.tagName === 'A') {
          const span = document.createElement('span');
          span.className = 'kh-pager__page is-current';
          span.setAttribute('aria-current', 'page');
          span.textContent = String(page);
          item.replaceWith(span);
        } else if (!isCurrent && item.tagName === 'SPAN') {
          const link = document.createElement('a');
          link.className = 'kh-pager__page';
          link.href = this.pageUrl(Number(label));
          link.textContent = label;
          item.replaceWith(link);
        }
      });
    }

    pageUrl(page) {
      const params = new URLSearchParams(window.location.search);
      params.delete('section_id');
      if (page > 1) {
        params.set('page', String(page));
      } else {
        params.delete('page');
      }
      const query = params.toString();
      return window.location.pathname + (query ? '?' + query : '') + '#results';
    }

    // The panel is a `<details>` so it can collapse on small screens. Chrome
    // now hides closed details content with `content-visibility: hidden`,
    // which no `display` rule on the child can override, so on desktop the
    // element is genuinely opened rather than merely styled open.
    keepOpenOnDesktop() {
      const details = this.querySelector('[data-facets-details]');
      if (!details) return;

      const desktop = window.matchMedia('(min-width: 990px)');
      const sync = () => {
        if (desktop.matches && !details.open) details.open = true;
      };

      sync();
      desktop.addEventListener('change', sync);
      details.addEventListener('toggle', sync);
    }

    isFacetForm(form) {
      return !!form && !form.closest('[data-facets-results]');
    }

    onChange(event) {
      if (!this.isFacetForm(event.target.closest('form'))) return;
      if (event.target.matches('input[type="number"], input[type="range"]')) {
        window.clearTimeout(this.debounce);
        this.debounce = window.setTimeout(() => this.apply(), 500);
        return;
      }
      this.apply();
    }

    onClick(event) {
      // A long value list ships collapsed; the toggle reveals the rest in
      // place rather than giving the panel its own scrollbar.
      const more = event.target.closest('[data-facet-more]');
      if (more) {
        const facet = more.closest('.kh-facet');
        const expanded = more.getAttribute('aria-expanded') === 'true';
        more.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (facet) facet.classList.toggle('is-expanded', !expanded);
        return;
      }

      // With the virtual ordering on, a pager link only changes which slice of
      // the two runs is shown - there is nothing to ask the server for.
      const pager = event.target.closest('.kh-pager__page, .kh-pager__arrow');
      if (pager && this.virtual) {
        const page = Number(new URL(pager.href, window.location.origin).searchParams.get('page')) || 1;
        event.preventDefault();
        this.renderVirtual(page, true);
        const results = document.querySelector('[data-facets-results]');
        if (results) results.scrollIntoView({ block: 'start', behavior: 'smooth' });
        return;
      }

      const link = event.target.closest('[data-facet-link]');
      if (!link) return;
      event.preventDefault();
      this.render(link.href.replace(/^[^?]*/, ''), true, true);
    }

    apply() {
      // Every form, not the first one: sorting lives in a form of its own
      // outside the collapsing filter panel, and reading only `querySelector`
      // silently dropped whichever of the two came second.
      const forms = Array.from(this.querySelectorAll('form')).filter((form) => this.isFacetForm(form));
      if (!forms.length) return;
      const range = this.querySelector('kh-price-range');
      const skipPrice = range && typeof range.isFullSpan === 'function' && range.isFullSpan();
      const params = new URLSearchParams();
      const seen = new Set();
      forms.forEach(function (form) {
        new FormData(form).forEach(function (value, key) {
          if (String(value).trim() === '') return;
          if (skipPrice && key.indexOf('filter.v.price') === 0) return;
          // `q` and `options[prefix]` are repeated in both forms so each one
          // still works on its own without JavaScript.
          if (key === 'q' || key === 'options[prefix]' || key === 'sort_by') {
            if (seen.has(key)) return;
            seen.add(key);
          }
          params.append(key, value);
        });
      });
      /* No scroll: ticking a box used to throw the page down to the grid, so
         on a phone - where the panel is a collapsed `<details>` and the whole
         screen is filters - setting a second filter meant scrolling back up
         every time. The chips above the grid report the effect instead. */
      this.render('?' + params.toString(), true, false);
    }

    render(search, push, scroll) {
      const target = document.querySelector('[data-facets-results]');
      const url = window.location.pathname + search;
      this.classList.add('is-loading');
      if (target) target.setAttribute('aria-busy', 'true');

      fetch(url + (search.indexOf('?') === 0 ? '&' : '?') + 'section_id=' + this.sectionId)
        .then(function (response) {
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          // The results region carries the chips, so it is swapped first and
          // the count follows: both live outside the panel by design.
          ['[data-facets-results]', '[data-facets-panel]', '[data-facets-sort]', '[data-facets-count]'].forEach((selector) => {
            const next = parsed.querySelector(selector);
            const current = this.querySelector(selector) || document.querySelector(selector);
            if (next && current) current.innerHTML = next.innerHTML;
          });
          // Only the inner regions are swapped, so the counts the virtual
          // ordering runs on are refreshed from the fetched section root.
          const root = parsed.querySelector('kh-facets');
          if (root) {
            ['data-virtual-order', 'data-instock', 'data-total', 'data-per-page'].forEach((name) => {
              if (root.hasAttribute(name)) {
                this.setAttribute(name, root.getAttribute(name));
              } else {
                this.removeAttribute(name);
              }
            });
          }
          if (push) window.history.pushState({ search: search }, '', url);
          this.initVirtualOrder();
          observeReveals(document);
          KH.announce(KH.t('facets_updated'));

          /* Only when the shopper cannot see any of the grid - removing a chip
             from inside the results region leaves them looking straight at it,
             and scrolling then just moves the page under their eyes. */
          const results = document.querySelector('[data-facets-results]');
          if (scroll && results) {
            const box = results.getBoundingClientRect();
            const offscreen = box.top > window.innerHeight || box.bottom < 0;
            if (offscreen) results.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        })
        .catch(function (error) {
          console.error(error);
        })
        .finally(() => {
          this.classList.remove('is-loading');
          if (target) target.removeAttribute('aria-busy');
        });
    }
  }

  customElements.define('kh-facets', KhFacets);

  /* ---------- <kh-price-range> ---------- */

  class KhPriceRange extends HTMLElement {
    connectedCallback() {
      this.min = this.querySelector('[data-range-min]');
      this.max = this.querySelector('[data-range-max]');
      this.minNumber = this.querySelector('[data-number-min]');
      this.maxNumber = this.querySelector('[data-number-max]');
      this.track = this.querySelector('[data-range-track]');
      if (!this.min || !this.max) return;

      this.sync = this.sync.bind(this);
      this.min.addEventListener('input', this.sync);
      this.max.addEventListener('input', this.sync);
      if (this.minNumber) this.minNumber.addEventListener('input', () => this.fromNumbers());
      if (this.maxNumber) this.maxNumber.addEventListener('input', () => this.fromNumbers());
      this.sync();
    }

    fromNumbers() {
      if (this.minNumber) this.min.value = this.minNumber.value || this.min.min;
      if (this.maxNumber) this.max.value = this.maxNumber.value || this.max.max;
      this.paint();
    }

    sync() {
      let low = Number(this.min.value);
      let high = Number(this.max.value);
      if (low > high) {
        const swap = low;
        low = high;
        high = swap;
        this.min.value = low;
        this.max.value = high;
      }
      if (this.minNumber) this.minNumber.value = low;
      if (this.maxNumber) this.maxNumber.value = high;
      this.paint();
    }

    // True while both thumbs sit on the ends of the track. The panel submits
    // every field it holds, so without this an untouched price range would
    // attach a 0–max price filter to the URL of every other filter change.
    isFullSpan() {
      return Number(this.min.value) <= Number(this.min.min) && Number(this.max.value) >= Number(this.max.max);
    }

    // The fill is expressed as unitless fractions of the thumb travel, not as
    // percentages of the track: a thumb can only reach from half its own width
    // to half a width short of the far edge, so percentages of the full width
    // left the fill visibly out of step with the thumbs at both ends.
    paint() {
      if (!this.track) return;
      const floor = Number(this.min.min);
      const ceiling = Number(this.max.max) || 1;
      const span = Math.max(1, ceiling - floor);
      const left = Math.min(1, Math.max(0, (Number(this.min.value) - floor) / span));
      const right = Math.min(1, Math.max(0, (Number(this.max.value) - floor) / span));
      this.track.style.setProperty('--range-start', String(left));
      this.track.style.setProperty('--range-end', String(right));
      // With both thumbs at the top of the range the upper input covers the
      // lower one and the lower thumb can no longer be grabbed, so the one
      // nearest the pointer is lifted above the other.
      this.min.style.zIndex = left > 0.5 ? '3' : '2';
      this.max.style.zIndex = left > 0.5 ? '2' : '3';
    }
  }

  customElements.define('kh-price-range', KhPriceRange);

  /* ---------- <kh-gallery> ---------- */

  class KhGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-stage]');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.index = 0;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.select(i));
      });
      this.querySelectorAll('[data-gallery-step]').forEach((button) => {
        button.addEventListener('click', () => {
          this.select(this.index + Number(button.getAttribute('data-gallery-step')));
        });
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.select(this.index + 1);
        if (event.key === 'ArrowLeft') this.select(this.index - 1);
      });
      this.select(0);
      this.preload();
    }

    /* Slides after the first are lazy images inside display: none, so the
       browser only starts fetching one when its thumbnail is clicked - every
       click waited on the network. Once the page has loaded the rest are
       fetched in the background, and hovering or focusing a thumbnail jumps
       its photo to the front of the queue. */
    preload() {
      const warm = (slide) => {
        const img = slide && slide.querySelector('img');
        if (!img || img.dataset.warm) return;
        img.dataset.warm = '1';
        img.loading = 'eager';
        if (img.decode) img.decode().catch(() => {});
      };
      this.thumbs.forEach((thumb, i) => {
        const warmThis = () => warm(this.slides[i]);
        thumb.addEventListener('pointerenter', warmThis, { once: true });
        thumb.addEventListener('focus', warmThis, { once: true });
      });
      const warmAll = () => this.slides.forEach(warm);
      const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(warmAll, { timeout: 2000 }) : setTimeout(warmAll, 300));
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    }

    select(next) {
      if (!this.slides.length) return;
      const total = this.slides.length;
      const previous = this.index;
      this.index = ((next % total) + total) % total;
      if (this.stage && this.index !== previous) {
        this.stage.dataset.direction = this.index < previous ? 'prev' : 'next';
      }
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('hidden', i !== this.index);
      });
      this.thumbs.forEach((thumb, i) => {
        const active = i === this.index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.dispatchEvent(new CustomEvent('kh:gallery:change', { bubbles: true, detail: { index: this.index } }));
    }
  }

  customElements.define('kh-gallery', KhGallery);

  /* ---------- <kh-lightbox> ---------- */

  class KhLightbox extends KhDialog {
    connectedCallback() {
      super.connectedCallback();
      this.slides = Array.from(this.querySelectorAll('[data-lightbox-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));
      this.index = 0;
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.select(i)));
      this.querySelectorAll('[data-lightbox-step]').forEach((button) => {
        button.addEventListener('click', () => this.select(this.index + Number(button.getAttribute('data-lightbox-step'))));
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.select(this.index + 1);
        if (event.key === 'ArrowLeft') this.select(this.index - 1);
      });
      this.prime();
    }

    /* Waiting for the click to start fetching the large photo left a blank
       stage for a moment on every open. The photo the gallery is showing is
       fetched once the page is idle, and reaching for the gallery (hover,
       touch, focus) fetches it and its neighbours, so by the time the click
       lands the lightbox usually has its photo already. Skipped when the
       visitor has asked the browser to save data. */
    prime() {
      const trigger = document.querySelector('[data-dialog-open="' + this.id + '"]');
      const gallery = trigger ? trigger.closest('kh-gallery') : null;
      if (!gallery || !this.slides.length) return;
      this.sourceGallery = gallery;
      if (navigator.connection && navigator.connection.saveData) return;
      const current = () => (typeof gallery.index === 'number' ? gallery.index : 0);
      const around = (index) => {
        const total = this.slides.length;
        [0, 1, -1].forEach((offset) => this.warm(this.slides[(((index + offset) % total) + total) % total], index + offset));
      };
      let engaged = false;
      const engage = () => {
        engaged = true;
        around(current());
      };
      const stage = trigger.parentElement;
      stage.addEventListener('pointerenter', engage, { passive: true });
      stage.addEventListener('touchstart', engage, { passive: true });
      trigger.addEventListener('focus', engage);
      gallery.addEventListener('kh:gallery:change', (event) => {
        if (engaged) around(event.detail.index);
      });
      const idle = () => {
        const first = () => this.warm(this.slides[current()], current());
        if ('requestIdleCallback' in window) requestIdleCallback(first, { timeout: 2500 });
        else setTimeout(first, 500);
      };
      if (document.readyState === 'complete') idle();
      else window.addEventListener('load', idle, { once: true });
    }

    open(opener, index) {
      this.gallery = (opener && opener.closest('kh-gallery')) || this.sourceGallery || null;
      super.open(opener);
      if (typeof index === 'number') this.select(index);
      this.preload();
    }

    /* The lightbox photos are the 1800px renditions and sit hidden until
       shown, so lazy loading made every step wait on the network - the same
       stall the gallery had. Opening fetches the photo on screen and its
       neighbours first, then the rest once the browser is idle. */
    warm(slide, index) {
      const img = slide && slide.querySelector('img');
      if (!img || img.dataset.warm) return;
      img.dataset.warm = '1';
      /* Until the large photo arrives, the gallery's smaller copy - already
         decoded on the page - stands in behind it, so a slow fetch shows a
         softer photo rather than an empty stage. */
      const total = this.slides.length;
      const gallery = this.sourceGallery;
      const at = typeof index === 'number' ? (((index % total) + total) % total) : this.slides.indexOf(slide);
      const stand = gallery && gallery.slides && gallery.slides[at] ? gallery.slides[at].querySelector('img') : null;
      if (stand && stand.currentSrc && !img.complete) {
        img.style.backgroundImage = 'url("' + stand.currentSrc + '")';
        img.addEventListener('load', () => (img.style.backgroundImage = ''), { once: true });
      }
      img.loading = 'eager';
      if (img.decode) img.decode().catch(() => {});
    }

    preload() {
      const total = this.slides.length;
      if (!total) return;
      [0, 1, -1].forEach((offset) => this.warm(this.slides[(((this.index + offset) % total) + total) % total]));
      if (this.warmedAll) return;
      this.warmedAll = true;
      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('pointerenter', () => this.warm(this.slides[i]), { once: true });
      });
      const warmAll = () => this.slides.forEach((slide) => this.warm(slide));
      if ('requestIdleCallback' in window) requestIdleCallback(warmAll, { timeout: 1500 });
      else setTimeout(warmAll, 300);
    }

    onClose() {
      /* Browsing to the seventh photo in the lightbox and closing it used to
         drop you back on whichever photo the gallery was showing when you
         opened it. The gallery follows the lightbox instead, so closing feels
         like stepping back out of the same picture. */
      if (this.gallery && typeof this.gallery.select === 'function' && this.hasAttribute('open')) {
        this.gallery.select(this.index);
      }
      super.onClose();
    }

    select(next) {
      if (!this.slides.length) return;
      const total = this.slides.length;
      const previous = this.index;
      this.index = ((next % total) + total) % total;
      const stage = this.slides[0].parentElement;
      if (stage && this.index !== previous) {
        stage.dataset.direction = this.index < previous ? 'prev' : 'next';
      }
      this.slides.forEach((slide, i) => slide.toggleAttribute('hidden', i !== this.index));
      if (this.hasAttribute('open')) this.preload();
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.index);
        thumb.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      });
    }
  }

  customElements.define('kh-lightbox', KhLightbox);

  /* ---------- <kh-sticky-header> ---------- */

  class KhStickyHeader extends HTMLElement {
    connectedCallback() {
      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      /* Tabbing forward through the page scrolls it, which hid the header -
         so Shift+Tab back up put the keyboard on a menu item that was no
         longer on screen. Anything inside the header taking focus brings it
         back. */
      document.addEventListener('focusin', this.onFocusIn);
      setStickyOffset();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('focusin', this.onFocusIn);
    }

    onFocusIn(event) {
      if (this.contains(event.target)) this.setHidden(false);
    }

    /* Sticky columns reserve the header's height below it. While the header is
       slid away that reservation is an empty band, so the root carries the
       state and those columns move up into it. */
    setHidden(hidden) {
      this.toggleAttribute('data-hidden', hidden);
      document.documentElement.classList.toggle('kh-header-hidden', hidden);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        this.toggleAttribute('data-scrolled', y > 8);
        if (this.hasAttribute('data-hide-on-scroll')) {
          const delta = y - this.lastY;
          /* Rubber-band scrolling on a phone reports a pixel up, a pixel down,
             a pixel up. Reacting to every one of those made the header flick
             in and out while the finger was still resting on the screen, so a
             direction has to be held for a few pixels before it counts. */
          if (Math.abs(delta) > 6) {
            const locked = document.documentElement.classList.contains('kh-scroll-locked');
            const holdsFocus = this.contains(document.activeElement);
            this.setHidden(delta > 0 && y > 220 && !locked && !holdsFocus);
            this.lastY = y;
          }
        } else {
          this.lastY = y;
        }
        this.ticking = false;
      });
    }
  }

  customElements.define('kh-sticky-header', KhStickyHeader);

  /* ---------- <kh-recently-viewed> ---------- */

  const RECENT_KEY = 'kh:recently-viewed';

  KH.rememberProduct = function (handle) {
    if (!handle) return;
    /* A prerendered page runs its scripts before anyone has decided to visit
       it, so recording the visit here would fill "recently viewed" with sets
       the shopper only hovered over. The write is held until the page is
       actually activated; if it never is, it never happens. */
    if (document.prerendering) {
      document.addEventListener(
        'prerenderingchange',
        function () {
          KH.rememberProduct(handle);
        },
        { once: true }
      );
      return;
    }
    try {
      const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]').filter(function (item) {
        return item !== handle;
      });
      list.unshift(handle);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 20)));
    } catch (error) {
      /* storage unavailable - recently viewed simply does not persist */
    }
  };

  class KhRecentlyViewed extends HTMLElement {
    connectedCallback() {
      const exclude = this.getAttribute('data-exclude');
      const limit = Number(this.getAttribute('data-limit') || 8);
      const sectionId = this.getAttribute('data-section-id');
      let handles = [];
      try {
        handles = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      } catch (error) {
        handles = [];
      }
      handles = handles
        .filter(function (handle) {
          return handle && handle !== exclude;
        })
        .slice(0, limit);

      if (!handles.length) {
        this.hidden = true;
        return;
      }

      const query = handles
        .map(function (handle) {
          return 'handle:' + handle;
        })
        .join(' OR ');

      fetch(
        KH.root() +
          'search?section_id=' +
          sectionId +
          '&type=product&options%5Bprefix%5D=none&q=' +
          encodeURIComponent(query)
      )
        .then(function (response) {
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector('[data-recent-content]');
          const slot = this.querySelector('[data-recent-slot]');
          if (content && slot && content.children.length) {
            slot.innerHTML = content.innerHTML;
            this.hidden = false;
            observeReveals(this);
          } else {
            this.hidden = true;
          }
        })
        .catch(() => {
          this.hidden = true;
        });
    }
  }

  customElements.define('kh-recently-viewed', KhRecentlyViewed);

  /* ---------- <kh-cart-upsell> ---------- */

  /* The drawer ships with a server-rendered rail of sets from the same theme.
     If the shopper has looked at anything, that is the stronger offer, so the
     rail is replaced with those sets and the heading changes with it. The
     element fails silently: a failed fetch leaves the themed rail standing. */
  class KhCartUpsell extends HTMLElement {
    connectedCallback() {
      const slot = this.querySelector('[data-upsell-slot]');
      const label = this.querySelector('[data-upsell-heading-text]');
      if (!slot) return;

      const exclude = (this.getAttribute('data-exclude') || '').split(/\s+/).filter(Boolean);
      let handles = [];
      try {
        handles = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      } catch (error) {
        handles = [];
      }
      handles = handles
        .filter(function (handle) {
          return handle && exclude.indexOf(handle) === -1;
        })
        .slice(0, 6);

      if (!handles.length) return;

      const query = handles
        .map(function (handle) {
          return 'handle:' + handle;
        })
        .join(' OR ');

      fetch(
        KH.root() +
          'search?section_id=cart-upsell&type=product&options%5Bprefix%5D=none&q=' +
          encodeURIComponent(query)
      )
        .then(function (response) {
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const content = parsed.querySelector('[data-upsell-content]');
          if (!content || !content.children.length) return;

          /* The search endpoint answers by relevance, not by recency, so the
             cards are re-ordered to match the order they were viewed in. */
          const cards = Array.prototype.slice.call(content.children);
          cards.sort(function (a, b) {
            const link = function (node) {
              const href = node.querySelector('a[href]');
              if (!href) return '';
              /* Handles carry non-ASCII characters (the trademark sign, Danish
                 vowels), so the href is percent-encoded and has to be decoded
                 before it can be matched against the stored handles. */
              const slug = href.getAttribute('href').split('?')[0].split('/').pop();
              try {
                return decodeURIComponent(slug);
              } catch (error) {
                return slug;
              }
            };
            return handles.indexOf(link(a)) - handles.indexOf(link(b));
          });

          slot.innerHTML = '';
          cards.forEach(function (card) {
            slot.appendChild(card);
          });
          if (label) {
            const heading = this.getAttribute('data-recent-heading');
            if (heading) label.textContent = heading;
          }
          this.hidden = false;
        })
        .catch(function () {
          /* keep the themed rail */
        });
    }
  }

  customElements.define('kh-cart-upsell', KhCartUpsell);

  /* ---------- <kh-catnav> ---------- */

  /* Type-ahead over the sidebar theme list. The list is fully rendered and
     every link works without this; the field only hides rows that do not
     match, so the control degrades to a plain list of links. */
  class KhCatnav extends HTMLElement {
    connectedCallback() {
      const field = this.querySelector('[data-catnav-search]');
      const list = this.querySelector('[data-catnav-list]');
      const empty = this.querySelector('[data-catnav-empty]');
      const more = this.querySelector('[data-catnav-more]');
      this.keepOpenOnDesktop();
      if (!list) return;

      /* Only the first few themes (and the one being viewed) are listed until
         the button is pressed; the rest are rendered but hidden. */
      let expanded = false;
      const syncMore = () => {
        const searching = !!(field && field.value.trim());
        list.classList.toggle('is-collapsed', !expanded && !searching);
        if (!more) return;
        more.hidden = searching;
        more.setAttribute('aria-expanded', String(expanded));
        const label = more.querySelector('[data-catnav-more-label]');
        if (label) label.textContent = more.getAttribute(expanded ? 'data-label-less' : 'data-label-more');
      };

      if (more) {
        more.addEventListener('click', () => {
          expanded = !expanded;
          syncMore();
          /* Collapsing a long list from its end would leave the visitor
             stranded far below the card they were using. */
          if (!expanded) this.scrollIntoView({ block: 'nearest' });
        });
      }

      if (!field) return;

      const items = Array.prototype.slice.call(list.querySelectorAll('[data-catnav-item]'));

      const filter = () => {
        /* Folded to lowercase and stripped of accents so "pa" finds "På" and
           a Danish keyboard is not a requirement for searching. */
        const term = field.value
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        let shown = 0;
        items.forEach(function (item) {
          const label = (item.getAttribute('data-label') || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
          const match = !term || label.indexOf(term) !== -1;
          item.hidden = !match;
          if (match) shown += 1;
        });
        if (empty) empty.hidden = shown !== 0;
        /* A search looks through every theme, not just the ones on show. */
        syncMore();
      };

      field.addEventListener('input', filter);
      /* Escape clears rather than closing the drawer the field may sit in. */
      field.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape' || !field.value) return;
        event.stopPropagation();
        field.value = '';
        filter();
      });
    }

    /* The card ships closed so it costs a phone nothing. On desktop it is a
       sidebar with room to spare, and a closed list there is just an extra
       click - so it is genuinely opened rather than styled open, for the same
       `content-visibility` reason the filter panel is. */
    keepOpenOnDesktop() {
      const details = this.querySelector('[data-catnav-details]');
      if (!details) return;

      const desktop = window.matchMedia('(min-width: 990px)');
      const sync = () => {
        if (desktop.matches && !details.open) details.open = true;
      };

      sync();
      desktop.addEventListener('change', sync);
      details.addEventListener('toggle', sync);
    }
  }

  customElements.define('kh-catnav', KhCatnav);

  /* ---------- <kh-variants> ---------- */

  class KhVariants extends HTMLElement {
    connectedCallback() {
      this.productId = this.getAttribute('data-product-id');
      this.sectionId = this.getAttribute('data-section-id');
      this.addEventListener('change', this.onChange.bind(this));
    }

    onChange() {
      /* The Section Rendering API identifies a selection by option value id,
         comma-joined into a single parameter. This used to send the visible
         labels ("750 kr") as repeated option_values parameters, which the API
         cannot match - so every switch re-rendered the default variant and the
         picker snapped straight back to it. */
      const chosen = Array.from(this.querySelectorAll('input[type="radio"]:checked'))
        .map(function (input) {
          return input.getAttribute('data-option-value-id');
        })
        .filter(Boolean);
      if (!chosen.length) return;

      const params = new URLSearchParams();
      params.append('option_values', chosen.join(','));
      params.append('section_id', this.sectionId);

      /* The option radios live inside the region that gets re-rendered, so the
         button the shopper just pressed stops existing. Its id survives the
         round trip, which is enough to put the keyboard back where it was -
         otherwise picking a size with the arrow keys dropped focus to the top
         of the page and the group could only be used once. */
      const activeId = document.activeElement ? document.activeElement.id : null;

      /* Clicking through three sizes quickly fired three requests, and
         whichever answered last won - which is not necessarily the one that is
         selected. The older answers are dropped. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      fetch(window.location.pathname + '?' + params.toString(), { signal: this.controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          ['[data-variant-price]', '[data-variant-stock]', '[data-variant-cta]', '[data-variant-meta]', '[data-variant-notify]'].forEach(
            function (selector) {
              const next = parsed.querySelector(selector);
              const current = document.querySelector(selector);
              if (next && current) current.innerHTML = next.innerHTML;
            }
          );
          /* Without this the address bar still pointed at the default variant:
             a shopper who picked one and sent the link, or simply reloaded,
             lost the choice without being told. The id comes from the form
             that was just swapped in, so it is whatever the server resolved. */
          const idInput = document.querySelector('.kh-atc__form [name="id"]');
          if (idInput && idInput.value && window.history.replaceState) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', idInput.value);
            window.history.replaceState({}, '', url.toString());
          }

          if (activeId) {
            const again = document.getElementById(activeId);
            if (again) again.focus();
          }

          /* Price, stock line and button all change at once and none of it is
             spoken on its own. */
          const button = document.querySelector('.kh-atc__button');
          const price = document.querySelector('[data-variant-price]');
          let summary = '';
          if (button && button.disabled) {
            summary = button.textContent.trim();
          } else if (price) {
            summary = price.textContent.replace(/\s+/g, ' ').trim();
          }
          if (summary) KH.announce(KH.t('variant_changed', { summary: summary }));
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') console.error(error);
        });
    }
  }

  customElements.define('kh-variants', KhVariants);

  /* ---------- <kh-accordion-group> ---------- */

  class KhAccordionGroup extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-exclusive')) return;
      this.addEventListener('toggle', (event) => {
        const opened = event.target;
        if (!opened.open) return;
        this.querySelectorAll('details[open]').forEach(function (node) {
          if (node !== opened) node.open = false;
        });
      }, true);
    }
  }

  customElements.define('kh-accordion-group', KhAccordionGroup);

  /* ---------- <kh-readmore> ---------- */

  /* Clamps a block of prose and reveals the rest on demand. The full text is
     always in the DOM - only its height is limited - so crawlers and screen
     readers see one complete copy. The toggle stays hidden when the text is
     short enough to fit, and is re-measured on resize. */
  class KhReadMore extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-readmore-body]');
      this.toggle = this.querySelector('[data-readmore-toggle]');
      if (!this.body || !this.toggle) return;

      this.body.classList.add('kh-desc__body--clamped');
      this.toggle.addEventListener('click', () => this.setExpanded(this.toggle.getAttribute('aria-expanded') !== 'true'));
      this.sync();

      this.resizeTimer = null;
      this.onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => {
          if (this.toggle.getAttribute('aria-expanded') === 'true') return;
          this.sync();
        }, 200);
      };
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    sync() {
      const overflows = this.body.scrollHeight - this.body.clientHeight > 4;
      this.toggle.hidden = !overflows;
      if (!overflows) this.setExpanded(false);
    }

    setExpanded(expanded) {
      this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.body.classList.toggle('kh-desc__body--clamped', !expanded);
    }
  }

  customElements.define('kh-readmore', KhReadMore);

  /* ---------- <kh-hint> ---------- */

  /* An empty search field is a dead end: the customer has to guess what this
     shop even sells. This cycles a few real examples through the placeholder
     so the field suggests its own answers. It stops the moment the field is
     in use, and never starts when the visitor asked for less motion. */
  class KhHint extends HTMLElement {
    connectedCallback() {
      let hints = [];
      try {
        hints = JSON.parse(this.getAttribute('data-hints') || '[]');
      } catch (error) {
        hints = [];
      }

      this.hints = hints.filter(function (hint) {
        return typeof hint === 'string' && hint.length > 0;
      });
      this.target = this.querySelector('input, [data-hint-target]');
      if (!this.target || this.hints.length < 2) return;

      this.isInput = this.target.tagName === 'INPUT';
      this.index = 0;
      this.paused = false;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (this.isInput) {
        this.target.addEventListener('focus', () => { this.paused = true; });
        this.target.addEventListener('blur', () => { this.paused = this.target.value !== ''; });
      }

      this.timer = window.setInterval(this.next.bind(this), 3600);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    next() {
      if (this.paused || document.hidden) return;
      this.index = (this.index + 1) % this.hints.length;
      const hint = this.hints[this.index];

      if (this.isInput) {
        this.target.placeholder = hint;
        return;
      }

      this.target.classList.add('is-swapping');
      window.setTimeout(() => {
        this.target.textContent = hint;
        this.target.classList.remove('is-swapping');
      }, 180);
    }
  }

  customElements.define('kh-hint', KhHint);

  /* ---------- <kh-nav> ---------- */

  /* The menus are <details> so they work without JS and stay operable from the
     keyboard. On a device with a real pointer that is one click too many, so
     this opens them on hover instead. The close is delayed by a beat: the
     panel sits below the pill with a gap, and yanking it away mid-diagonal is
     the classic way to make a menu feel hostile. */
  class KhNav extends HTMLElement {
    connectedCallback() {
      this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.timer = null;

      this.addEventListener('pointerover', this.onOver.bind(this));
      this.addEventListener('pointerout', this.onOut.bind(this));
      this.addEventListener('focusin', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onBlur.bind(this));
      this.addEventListener('keydown', this.onKey.bind(this));

      /* One menu at a time, whichever way it was opened. */
      this.addEventListener('toggle', (event) => {
        if (!event.target.open) return;
        this.closeOthers(event.target);
      }, true);

      /* Without a pointer that can hover - a tablet, or a laptop in touch mode
         - nothing ever closed an open panel except tapping its own heading, so
         it stayed over the page while the shopper read what was underneath. */
      this.onOutside = (event) => {
        if (this.contains(event.target)) return;
        this.closeOthers(null);
      };
      document.addEventListener('pointerdown', this.onOutside);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside);
    }

    get menus() {
      return this.querySelectorAll('details.kh-header__dropdown');
    }

    closeOthers(keep) {
      this.menus.forEach(function (menu) {
        if (menu !== keep) menu.open = false;
      });
    }

    onOver(event) {
      if (!this.hoverable.matches) return;
      const menu = event.target.closest('details.kh-header__dropdown');
      window.clearTimeout(this.timer);
      if (!menu) {
        this.scheduleClose();
        return;
      }
      if (!menu.open) {
        menu.open = true;
        this.closeOthers(menu);
      }
    }

    onOut(event) {
      if (!this.hoverable.matches) return;
      /* relatedTarget is null when the pointer leaves the window entirely. */
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.scheduleClose();
    }

    scheduleClose() {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => {
        if (this.contains(document.activeElement)) return;
        this.closeOthers(null);
      }, 220);
    }

    onFocus(event) {
      const menu = event.target.closest('details.kh-header__dropdown');
      window.clearTimeout(this.timer);
      if (menu && !menu.open) {
        menu.open = true;
      }
      this.closeOthers(menu);
    }

    onBlur(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.closeOthers(null);
    }

    onKey(event) {
      if (event.key !== 'Escape') return;
      const menu = event.target.closest('details.kh-header__dropdown');
      if (!menu || !menu.open) return;
      menu.open = false;
      const summary = menu.querySelector('summary');
      if (summary) summary.focus();
    }
  }

  customElements.define('kh-nav', KhNav);

  /* ---------- Terms acceptance (e-mærket) ---------- */

  /* snippets/cart-terms.liquid puts an unticked, required "accept the terms"
     box in the cart page's and the drawer's checkout form. The browser blocks
     the submit by itself; this swaps its bubble (which points at an invisible
     input) for the inline message, remembers the choice while the drawer is
     re-rendered, and keeps the express buttons ([data-terms-gate]) inert until
     the box is ticked, since they skip the form's submit entirely. */
  let termsAccepted = false;

  function setTermsError(wrap, on) {
    if (!wrap) return;
    wrap.toggleAttribute('data-invalid', on);
    const message = wrap.querySelector('[data-terms-error]');
    if (message) message.hidden = !on;
  }

  KH.applyTerms = function () {
    document.querySelectorAll('[data-terms-check]').forEach(function (box) {
      box.checked = termsAccepted;
    });
    document.querySelectorAll('[data-terms-gate]').forEach(function (gate) {
      gate.toggleAttribute('data-locked', !termsAccepted);
      if (gate.firstElementChild) gate.firstElementChild.inert = !termsAccepted;
    });
    if (termsAccepted) {
      document.querySelectorAll('[data-terms]').forEach(function (wrap) {
        setTermsError(wrap, false);
      });
    }
  };

  function syncTermsFromPage() {
    /* The back/forward cache and form restore can bring a page back with
       the box already ticked; trust what the shopper sees. */
    termsAccepted = !!document.querySelector('[data-terms-check]:checked');
    KH.applyTerms();
  }

  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-terms-check]')) return;
    termsAccepted = event.target.checked;
    KH.applyTerms();
  });

  document.addEventListener(
    'invalid',
    function (event) {
      if (!event.target.matches || !event.target.matches('[data-terms-check]')) return;
      event.preventDefault();
      setTermsError(event.target.closest('[data-terms]'), true);
      event.target.focus();
    },
    true
  );

  /* The inert buttons can't be clicked, so the click lands on the gate. */
  document.addEventListener('click', function (event) {
    const gate = event.target.closest && event.target.closest('[data-terms-gate][data-locked]');
    if (!gate) return;
    const form = gate.closest('form');
    const box = form && form.querySelector('[data-terms-check]');
    if (!box) return;
    setTermsError(box.closest('[data-terms]'), true);
    box.focus();
  });

  /* "Acceptér" in the terms popup ticks the box it was opened from; the
     button's data-dialog-close closes the popup. */
  document.addEventListener('click', function (event) {
    const accept = event.target.closest && event.target.closest('[data-terms-accept]');
    if (!accept) return;
    const wrap = accept.closest('[data-terms]');
    const box = wrap && wrap.querySelector('[data-terms-check]');
    if (!box || box.checked) return;
    box.checked = true;
    box.dispatchEvent(new Event('change', { bubbles: true }));
  });

  window.addEventListener('pageshow', syncTermsFromPage);

  /* ---------- Cart bubble sync ---------- */

  document.addEventListener('kh:cart:updated', function (event) {
    const count = event.detail && typeof event.detail.item_count === 'number' ? event.detail.item_count : null;
    if (count === null) return;
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.toggleAttribute('hidden', count === 0);
    });
  });

  /* ---------- Horizontal strips that can be scrolled ---------- */

  /* The announcement bar and the navigation row both turn into swipeable
     strips on a narrow screen, with their scrollbars hidden so they do not
     draw a grey line across the design. That left the content simply cut off
     at the edge with nothing to say it continued. Each strip now fades on
     whichever side still has something behind it, and stops fading once that
     end is reached. */
  function markScrollEdges(strip) {
    /* A strip in a background tab measures zero wide, which would read as
       "everything overflows" and fade both ends of a row that fits. The
       ResizeObserver runs again the moment the tab is shown. */
    if (strip.clientWidth === 0) return;
    const max = strip.scrollWidth - strip.clientWidth;
    if (max < 6) {
      strip.removeAttribute('data-overflow-start');
      strip.removeAttribute('data-overflow-end');
      return;
    }
    strip.toggleAttribute('data-overflow-start', strip.scrollLeft > 4);
    strip.toggleAttribute('data-overflow-end', strip.scrollLeft < max - 4);
  }

  function initScrollEdges(root) {
    (root || document).querySelectorAll('[data-scroll-edges]').forEach(function (strip) {
      if (strip.khEdges) return;
      strip.khEdges = true;
      const update = function () {
        markScrollEdges(strip);
      };
      strip.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(strip);
      update();
    });
  }

  /* ---------- Best sellers of a collection ---------- */

  /* Best selling is a sort order rather than something a product carries, so
     the only way to know which five they are is to ask for the collection
     sorted that way. It is fetched after the page rather than before it: the
     grid is what the shopper asked for, and nothing above it may delay it.
     The slot holds its own height, so the arrival does not move the page. */

  function initPopularRails() {
    document.querySelectorAll('[data-popular-slot]').forEach(function (slot) {
      const url = slot.getAttribute('data-popular-url');
      if (!url || slot.hasAttribute('data-loaded')) return;
      slot.setAttribute('data-loaded', '');
      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('popular');
          return response.text();
        })
        .then(function (html) {
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const rail = parsed.querySelector('.kh-popular');
          /* A collection can come back empty - everything in it sold out, or
             the request failed. The slot then collapses to nothing rather than
             leaving a reserved hole with no content in it. */
          if (!rail) {
            slot.style.minHeight = '0';
            return;
          }
          slot.appendChild(document.importNode(rail, true));
          slot.style.minHeight = '0';
          KH.observeReveals(slot);
        })
        .catch(function () {
          slot.style.minHeight = '0';
        });
    });
  }

  /* ---------- Announcement bar ---------- */

  /* Phone-only. On a wide screen every promise in the bar is already on
     screen at once and moving them would be noise; below 750px they are
     stacked one per view, and something has to turn the page. */
  function initAnnounceRotation() {
    document.querySelectorAll('[data-announce-rotate]').forEach(function (list) {
      if (list.hasAttribute('data-rotating')) return;
      list.setAttribute('data-rotating', '');

      const phone = window.matchMedia('(max-width: 749px)');
      const still = window.matchMedia('(prefers-reduced-motion: reduce)');
      let timer = null;
      let paused = false;

      function stop() {
        window.clearInterval(timer);
        timer = null;
      }

      function advance() {
        if (paused || document.hidden) return;
        const max = list.scrollWidth - list.clientWidth;
        if (max < 4) return;
        /* Back to the start rather than reversing: the items are a list, not
           a there-and-back, and the first one is the one worth returning to. */
        const next = list.scrollLeft >= max - 4 ? 0 : list.scrollLeft + list.clientWidth;
        list.scrollTo({ left: next, behavior: still.matches ? 'auto' : 'smooth' });
      }

      function sync() {
        stop();
        if (!phone.matches || list.children.length < 2) return;
        timer = window.setInterval(advance, 5000);
      }

      /* Any sign the reader is handling it themselves - a swipe, a tab into a
         link - and the bar stops moving under them for good. Restarting after
         a timeout would take the text away mid-sentence. */
      ['pointerdown', 'touchstart', 'wheel', 'focusin'].forEach(function (type) {
        list.addEventListener(
          type,
          function () {
            paused = true;
            stop();
          },
          { passive: true, once: true }
        );
      });

      phone.addEventListener('change', sync);
      sync();
    });
  }

  /* ---------- Collapsed panels ---------- */

  /* A <details> is the theme's no-JavaScript way of hiding a secondary form:
     password recovery, a new address, editing one. Two things went wrong with
     that on their own.

     A link pointing at a closed panel did nothing visible - "Glemt din
     adgangskode?" scrolled to a summary and left it shut, so it read as a
     dead link. And a form inside one posts, fails validation, and comes back
     on a freshly loaded page with the panel closed again: the error message is
     rendered, correctly, inside something nobody can see. The shopper presses
     save, the page blinks, nothing happens, and there is no way to find out
     why. */

  function openDetails(details) {
    if (!details || details.open) return false;
    details.open = true;
    return true;
  }

  function revealFormErrors() {
    const error = document.querySelector('.kh-form-error');
    if (!error) return;
    let panel = error.closest('details');
    while (panel) {
      openDetails(panel);
      panel = panel.parentElement ? panel.parentElement.closest('details') : null;
    }
    /* The alert is what the page is now about, so it is both scrolled to and
       given focus - role="alert" only speaks if the node was already there. */
    error.setAttribute('tabindex', '-1');
    error.scrollIntoView({ block: 'center' });
    error.focus();
  }

  document.addEventListener('click', function (event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href').slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    const details = target.tagName === 'DETAILS' ? target : target.closest('details');
    if (!details) return;
    openDetails(details);
    const summary = details.querySelector('summary');
    if (summary) summary.focus();
  });

  /* ---------- Bootstrap ---------- */

  function boot() {
    document.documentElement.classList.remove('kh-no-js');
    measure();
    observeReveals(document);
    const product = document.querySelector('[data-remember-product]');
    if (product) KH.rememberProduct(product.getAttribute('data-remember-product'));
    initHoverPrefetch();
    initScrollEdges(document);
    initPopularRails();
    initAnnounceRotation();
    revealFormErrors();
    syncTermsFromPage();
  }

  document.documentElement.classList.add('kh-no-js');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  let resizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(measure, 150);
  });

  document.addEventListener('shopify:section:load', function (event) {
    observeReveals(event.target);
    initScrollEdges(event.target);
    measure();
  });
})();
